import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { money } from "../../lib/casePath";
import { BORR, SL, canMove } from "../../lib/stages";
import { Btn } from "../kit/Kit";

export const MOTIVOS = { capacidad_pago: "Capacidad de pago", buro: "Buró de crédito", documentacion: "Documentación", riesgo_regional: "Riesgo regional",
  concentracion: "Concentración", garantia: "Garantía insuficiente", politica: "Política", desistio: "Desistió el cliente", otro: "Otro" };
const lblM = "mt-3 block text-[11px] font-semibold uppercase tracking-widest text-[#8C867A]";
export const MotivoSelect = ({ value, onChange }) => (
  <label className={lblM}>Motivo del rechazo
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inpCls} data-testid="motivo-rechazo">
      <option value="">Selecciona…</option>{Object.entries(MOTIVOS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
);


export const inpCls = "mt-1.5 w-full rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#E8643C] focus:bg-white";

const useEsc = (onClose) => useEffect(() => {
  const h = (e) => e.key === "Escape" && onClose && onClose();
  window.addEventListener("keydown", h);
  return () => window.removeEventListener("keydown", h);
}, [onClose]);

export const Modal = ({ title, sub, onClose, children, testid, wide }) => {
  useEsc(onClose);
  return (
  <div className="fixed inset-0 z-[60] grid place-items-center bg-[#111]/40 p-4 backdrop-blur-sm" data-testid={testid} style={{ animation: "rise .2s ease-out both" }}>
    <div className={`max-h-[92vh] w-full overflow-y-auto rounded-3xl bg-white p-8 shadow-xl ${wide ? "max-w-3xl" : "max-w-md"}`}>
      <div className="flex items-start justify-between gap-4"><h3 className="font-serif-display text-2xl font-bold">{title}</h3>
        <button onClick={onClose} className="rounded-lg p-1 text-[#8C867A] hover:bg-[#F2F0EC]" data-testid={`${testid}-close`}><X size={18} /></button></div>
      {sub && <p className="mt-1 text-sm text-[#5C5850]">{sub}</p>}
      {children}
    </div>
  </div>
  );
};

const KIND = {
  enviar: ["Enviar a revisión", "Se crea la versión con su PDF y se avisa al Director de Crédito.", false],
  aprobar: ["Aprobar deal", "Quedará bloqueado. Comentario opcional.", false],
  devolver: ["Devolver para corrección", "Vuelve a Análisis con el comentario visible para el analista.", true],
  rechazar: ["Rechazar en definitiva", "El deal queda Rechazado y bloqueado.", true],
  disponer: ["Registrar disposición", "Indica la fecha y el monto efectivamente dispuesto.", false],
};

export const useStageFlow = (onDone) => {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [com, setCom] = useState("");
  const [motivo, setMotivo] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [monto, setMonto] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (deal, to, extra = {}) => {
    setBusy(true);
    try {
      const r = await http.post(`/expedientes/${deal.id}/stage`, { stage: to, ...extra });
      toast.success(`Movido a «${SL[r.data.stage]?.l || SL[to].l}»`);
      setP(null);
      onDone?.(r.data);
    } catch (e) { toast.error(errMsg(e)); onDone?.(null); } finally { setBusy(false); }
  };
  const ask = (deal, to) => {
    const [ok, why] = canMove(user, deal, to);
    if (!ok) { if (why) toast.error(why); return; }
    const kind = BORR.includes(to) && !BORR.includes(deal.stage) ? "devolver"
      : { aprobado: "aprobar", rechazado: "rechazar", dispuesto: "disponer", en_revision: "enviar" }[to];
    if (!kind) { send(deal, to); return; }
    setCom(""); setMotivo(""); setMonto(String(deal.recursos || "")); setP({ deal, to, kind });
  };
  const confirm = () => {
    const [, , req] = KIND[p.kind];
    if (req && com.trim().length < 3) { toast.error("El comentario es obligatorio"); return; }
    if (p.kind === "rechazar" && !motivo) { toast.error("Selecciona el motivo del rechazo"); return; }
    if (p.kind === "disponer" && (!fecha || !(Number(monto) > 0))) { toast.error("Indica fecha y monto"); return; }
    send(p.deal, p.to, { comentario: com, ...(p.kind === "rechazar" ? { motivo } : {}), ...(p.kind === "disponer" ? { fecha, monto: Number(monto) } : {}) });
  };
  const modal = p && (
    <Modal title={KIND[p.kind][0]} sub={`${p.deal.nombre} · ${KIND[p.kind][1]}`} onClose={() => setP(null)} testid="stage-modal">
      {p.kind === "disponer" ? (<>
        <label className="mt-5 block text-xs font-semibold uppercase tracking-widest text-[#8C867A]">Fecha de disposición
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={inpCls} data-testid="stage-modal-fecha" /></label>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-widest text-[#8C867A]">Monto dispuesto (MXN)
          <input type="number" value={monto} onChange={(e) => setMonto(e.target.value)} className={inpCls} data-testid="stage-modal-monto" /></label>
        <p className="mt-1 text-xs text-[#8C867A]">{money(Number(monto) || 0)}</p>
      </>) : p.kind !== "enviar" && (
        <textarea rows={3} value={com} onChange={(e) => setCom(e.target.value)} className={inpCls} data-testid="stage-modal-comment"
          placeholder={KIND[p.kind][2] ? "Comentario (obligatorio)" : "Comentario (opcional)"} />
      )}
      {p.kind === "rechazar" && <MotivoSelect value={motivo} onChange={setMotivo} />}
      <div className="mt-6 flex justify-end gap-3"><Btn kind="secondary" onClick={() => setP(null)}>Cancelar</Btn>
        <Btn kind={p.kind === "rechazar" ? "primary" : "accent"} disabled={busy} onClick={confirm} data-testid="stage-modal-confirm">{KIND[p.kind][0]}</Btn></div>
    </Modal>
  );
  return [ask, modal, busy];
};
